import { AgreementsStorageService } from './agreements-storage.service';
import { AgreementGroupModel } from './agreement-group.model';
import { AgreementStorageDataModel } from './agreement-storage-data.model';
import { Inject, Injectable } from '@angular/core';
import { IAgreementSet } from '../../interfaces/agreements.interface';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

@Injectable()
export class AgreementsService {
  private storage: AgreementsStorageService = new AgreementsStorageService();
  private consentForm: FormGroup;
  public groups: Array<AgreementGroupModel> = [];
  public allGroupId = 99999999999;
  public allGroupDataInstance: AgreementStorageDataModel = this.storage.createGroupStorage(this.allGroupId);
  public agreementsGroupsLength: number;

  constructor(
    @Inject('Endpoint') private endpoint: any,
    private formBuilder: FormBuilder
  ) {
  }
  public async initializeAgreements(): Promise<FormGroup> {
    try {
      const response = await this.endpoint('agreements.get').run();
      this.prepare(response);
      this.createConsentForm(this.groups);
      return this.consentForm;
    } catch (error) {
      throw error;
    }
  }

  public prepare(agreementsData: IAgreementSet[] = []): void {
    this.clear();
    this.agreementsGroupsLength = agreementsData.length;
    this.sortAgreements(agreementsData);

    agreementsData.forEach((agreementGroup: IAgreementSet) => {
      const groupModel: AgreementGroupModel = new AgreementGroupModel(agreementGroup, this.storage, this.allGroupDataInstance);
      this.groups.push(groupModel);
    });
  }

  public sendSelectionList(selectedAgreements: string[]): void {
    this.endpoint('agreements.update', {selectedAgreements})
      .run()
      .then()
      .catch((error: any) => {
        console.error(error);
      });
  }

  public validateAllFormFields(): void {
    const formGroup: FormGroup = this.consentForm;

    const markControlsAsTouchedAndExpand = (group: FormGroup | FormArray) => {
      Object.keys(group.controls).forEach(field => {
        const control = group.get(field);
        if (control instanceof FormControl) {
          control.markAsTouched({ onlySelf: true });
        } else if (control instanceof FormGroup || control instanceof FormArray) {
          markControlsAsTouchedAndExpand(control);
        }
      });

      if (group instanceof FormGroup && group.get('expanded') && group.invalid) {
        const hasErrors = group.invalid;
        group.get('expanded').setValue(hasErrors ? true : group.get('expanded').value);
        this.allGroupDataInstance.expanded = true;
      }
    };

    markControlsAsTouchedAndExpand(formGroup);
  }

  public toggleList(form: FormGroup, groupIndex: number, event: Event): void {
    event.preventDefault();
    const consentGroup = form.get(['consentArray', groupIndex]) as FormGroup;

    if (consentGroup) {
      const isExpanded = consentGroup.get('expanded').value;
      consentGroup.get('expanded').setValue(!isExpanded);
      this.setListToggleElementLabel(form, groupIndex, !isExpanded);
    }
  }

  public setListToggleElementLabel(form: FormGroup, groupIndex: number, isExpanded: boolean): void {
    const consentGroup = form.get(['consentArray', groupIndex]) as FormGroup;

    if (consentGroup) {
      const label = (consentGroup.get('playAgreements') as FormArray).length > 1
        ? isExpanded ? 'Zwiń pełną treść zgód' : 'Rozwiń pełną treść zgód'
        : isExpanded ? 'Zwiń pełną treść zgody' : 'Rozwiń pełną treść zgody';
      consentGroup.get('srOnlyText').setValue(label);
    }
  }

  private createConsentForm(agreementGroups: AgreementGroupModel[]): void {
    this.consentForm = this.formBuilder.group({
      allConsent: [false],
      consentArray: this.formBuilder.array(this.createAgreementControls(agreementGroups))
    });
  }

  private createAgreementControls(agreementGroups: AgreementGroupModel[]): FormGroup[] {
    return agreementGroups.map((consent: AgreementGroupModel) => {
      return this.formBuilder.group({
        selected: [consent.selected, consent.required ? Validators.requiredTrue : null],
        expanded: [consent.expanded],
        title: [consent.title],
        tipMessage: [consent.tipMessage],
        playAgreements: this.formBuilder.array(this.createPlayAgreementControls(consent.playAgreements))
      });
    });
  }

  private createPlayAgreementControls(playAgreements: any[]): FormGroup[] {
    return playAgreements.map(agreement =>
      this.formBuilder.group({
        omsCode: [agreement.omsCode],
        name: [agreement.name],
        description: [agreement.description],
        selected: [agreement.selected, agreement.required ? Validators.requiredTrue : null],
        required: [agreement.required]
      })
    );
  }

  private clear(): void {
    this.groups = [];
    this.storage.clear();
  }

  private sortAgreementsArray(agreementsArray: any): void {
    agreementsArray.sort(function (agreement1: any, agreement2: any) {
      return agreement1.priority - agreement2.priority;
    });
  }

  private sortAgreements(agreementsData: any): void {
    this.sortAgreementsArray(agreementsData);

    agreementsData.forEach((agreementsGroup: AgreementGroupModel) => {
      this.sortAgreementsArray(agreementsGroup.playAgreements);
    });
  }

  public getAgreementsForm(): FormGroup {
    return this.consentForm;
  }
}
